// MA-891 — the badge look: ONE total function over the tree status, the badge rules,
// the latch and pick eligibility (KID-JOURNEY-2026-10-08 §5, D-KJ-8, D-KJ-16, D-KJ-19).
// Pure: no React.

import type { Status } from "../skillTreeStatus";
import type { TapEligibility } from "../journeyView";

export type BadgeLook = "gold" | "silver" | "working" | "later" | "hidden";
export const BADGE_LOOKS: readonly BadgeLook[] = ["gold", "silver", "working", "later", "hidden"];

/** Trophies and practice-day badges have two looks. */
export type MedalLook = "earned" | "later";
export const MEDAL_LOOKS: readonly MedalLook[] = ["earned", "later"];

/** The in-window tap kinds (journeyView.ts TapEligibility: `launch` and
 *  `narrate:zero-pool` are IN the D4 window; `narrate:beyond-*` are out). */
export function isInWindow(tap: TapEligibility): boolean {
  return tap === "launch" || tap === "narrate:zero-pool";
}

export interface SkillLookInput {
  status: Status;
  /** Silver earned now or latched. */
  silver: boolean;
  /** Gold earned now or latched. */
  gold: boolean;
  pickEligible: boolean;
  tapEligibility: TapEligibility;
}

/** The skill badge look. Precedence: gold (wins over every live status, suppressed
 *  included), silver, then the status. A `secure`/`mastered` tree status whose
 *  stricter kid rule has not landed yet (second-try evidence; a Foundation skill's
 *  same-day secure) reads as "working on it". */
export function skillBadgeLook(input: SkillLookInput): BadgeLook {
  if (input.gold) return "gold";
  if (input.silver) return "silver";
  switch (input.status) {
    case "suppressed":
      return "hidden";
    case "age_covered":
      return input.pickEligible ? "working" : "hidden";
    case "practicing":
    case "not_yet":
    case "secure":
    case "mastered":
      return "working";
    case "next":
      return isInWindow(input.tapEligibility) ? "working" : "later";
    case "locked":
      return "later";
    default: {
      // Exhaustiveness: a new Status member fails typecheck here.
      const never: never = input.status;
      throw new Error(`skillBadgeLook: unknown status ${String(never)}`);
    }
  }
}

export function medalLook(earned: boolean): MedalLook {
  return earned ? "earned" : "later";
}

/** MA-900 — the book-level live-QA hook (JourneyPath's `data-badge-summary` on the
 *  journey-path root): the catalogue's skill badges counted by look, in BADGE_LOOKS
 *  order, "<gold>|<silver>|<working>|<later>|<hidden>". Counts only, never an id or a
 *  name. The first four are the looks the book draws (one shelf cell per badge; the
 *  Next-up copies are not separate badges); hidden badges are in the catalogue but
 *  never drawn. Practice-day and trophy medals (MEDAL_LOOKS) are not skill badges and
 *  are not counted. Spec: docs/build/specs/MA-900-badge-summary-hook.md. */
export function badgeSummaryOf(skills: readonly { look: BadgeLook }[]): string {
  const n: Record<BadgeLook, number> = { gold: 0, silver: 0, working: 0, later: 0, hidden: 0 };
  for (const s of skills) n[s.look] += 1;
  return BADGE_LOOKS.map((l) => n[l]).join("|");
}
